// ─── FlujogramaWorkflowBar — wrapper shared para flujograma (ADR-025 ext 0661 + 0661.1 fix) ──
// Flujograma NO tiene versionado clásico (NO existe modelo VersionFlujograma BD ·
// confirmado por valoria-grill T-0 del prompt 0661). Por tanto NO renderiza pill
// "Ed. N" ni historial popover ni botón "Nueva edición".
//
// Usa el `<WorkflowBar>` shared canónico directamente (no a través de
// `<ArtefactoWorkflowBar>`) porque solo necesita la barra de acciones legales +
// confirm modal centralizado · NO la lógica de versiones.
//
// FIX 0661.1 — endpoints corregidos:
//   GET  /api/v1/flujogramas/:processId/latest             (estado actual · acepta cualquier estado)
//   POST /api/v1/flujogramas/edit/:flowchartId/transitar   (motor unificado ADR-025)
//
// Resultado del GET: { data: { id (= flowchartId), procesoId, estado, ... } }
// → derivamos flowchartId del response para construir el endpointTransitar.
//
// Reemplaza el hook legacy de transiciones del flujograma (152 LoC con
// confirmDialog/rejectOpen propios), eliminado en el refactor T-9 del prompt 0661.

import { useCallback, useEffect, useState } from 'react';
import { request } from '../../../../shared/services/api';
import { WorkflowBar, type EstadoWorkflow } from '../../../../shared/components/WorkflowBar';

interface FlujogramaInfo {
  id: string;
  estado: EstadoWorkflow;
}

interface FlujogramaApiResponse {
  data: {
    id: string;
    estado: string;
  } | null;
}

interface FlujogramaWorkflowBarProps {
  /** ID del proceso (UUID). El wrapper resuelve internamente el flowchartId vía GET. */
  procesoId: string;
  /** Si false, oculta el componente. Default true. */
  enabled?: boolean;
  /** Callback cada vez que cambia el estado tras una transición OK. */
  onEstadoChange?: (estado: EstadoWorkflow) => void;
}

const ESTADOS_VALIDOS: EstadoWorkflow[] = [
  'BORRADOR',
  'EN_REVISION',
  'APROBADO',
  'PUBLICADO',
  'RECHAZADO',
  'DESCARTADO',
  'ARCHIVADO',
];

function parseEstado(raw: string): EstadoWorkflow | null {
  return ESTADOS_VALIDOS.includes(raw as EstadoWorkflow) ? (raw as EstadoWorkflow) : null;
}

export function FlujogramaWorkflowBar({
  procesoId,
  enabled = true,
  onEstadoChange,
}: FlujogramaWorkflowBarProps) {
  const [flujograma, setFlujograma] = useState<FlujogramaInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    if (!procesoId) {
      setFlujograma(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // /latest devuelve el flujograma activo (cualquier estado · BORRADOR/EN_REVISION/APROBADO/PUBLICADO).
      const res = await request<FlujogramaApiResponse>(`/flujogramas/${procesoId}/latest`);
      if (!res.data) {
        setFlujograma(null);
        return;
      }
      const estado = parseEstado(res.data.estado);
      if (!estado) {
        setFlujograma(null);
        return;
      }
      setFlujograma({ id: res.data.id, estado });
    } catch {
      setFlujograma(null);
    } finally {
      setLoading(false);
    }
  }, [procesoId]);

  useEffect(() => {
    if (enabled) cargar();
  }, [enabled, cargar]);

  if (!enabled) return null;

  if (loading) {
    return <span style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>…</span>;
  }

  if (!flujograma) return null;

  return (
    <WorkflowBar
      artefactoTipo="FLUJOGRAMA"
      artefactoId={flujograma.id}
      estadoActual={flujograma.estado}
      endpointTransitar={`/flujogramas/edit/${flujograma.id}/transitar`}
      onTransicionOk={() => {
        cargar();
        if (onEstadoChange && flujograma) onEstadoChange(flujograma.estado);
      }}
      compact
    />
  );
}
